import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import {
  InfoCard,
  InfoCardVariant,
} from '@monorepo/shared/componentsV2/InfoCard';
import { StyledFormGroup } from '@monorepo/shared/componentsV2/styled/form';
import { useDataset } from '@monorepo/shared/hooks/datasets/useDataset';
import { bodyMedium } from '@monorepo/shared/styles/text';
import { WorkflowResponse } from 'mapistry-shared';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { DatasetInfo } from '../../shared/DatasetPicker/DatasetInfo';
import { findStepInputDataset } from '../stepUtils';
import {
  getDefaultConditions,
  getDefaultDateInterval,
  getDefaultJoinType,
  getTimeSeriesJoinType,
} from './helpers';
import { JoinToDatasetPicker } from './JoinToDatasetPicker';
import { StyledDescription, StyledH3 as StyledH3Base } from './styled';

const StyledH3 = styled(StyledH3Base)`
  margin-bottom: 0.5rem;
`;

const StyledLabel = styled.div`
  ${bodyMedium}
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const StyledJoinToTextContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0;
  font-weight: 600;
  line-height: 0px;
  border-bottom: 2px dashed ${({ theme }) => theme.colors.grayddd};
`;

const StyledJoinToText = styled.div`
  width: fit-content;
  padding: 0 0.5rem;
  margin-bottom: -0.5em;
  background: white;
`;

const StyledInfoCard = styled(InfoCard)`
  max-width: 32rem;
  margin-top: 1rem;
`;

interface JoinToModalPageProps {
  isNewStep?: boolean;
  setIsDataLoading: (isLoading: boolean) => void;
  workflow: WorkflowResponse;
  stepId?: string;
}

function JoinToModalPageContent({
  isNewStep,
  setIsDataLoading,
  stepId,
  workflow,
}: JoinToModalPageProps) {
  const {
    input: { value: selectedJoinToDatasetId },
    meta: { initial: initialJoinToDatasetId },
  } = useField<string>('joinToDatasetId');

  const {
    dataset: joinToDataset,
    isLoading,
    isError,
  } = useDataset({
    config: { useErrorBoundary: false },
    datasetId: selectedJoinToDatasetId,
    organizationId: workflow.organizationId,
  });

  const stepInputDataset = useMemo(
    () => findStepInputDataset(workflow, stepId),
    [stepId, workflow],
  );
  const isInputTimeSeries = useMemo(
    () => !!stepInputDataset.timeSeriesGranularity,
    [stepInputDataset],
  );

  const { input: dateIntervalInput } = useField<string>('dateInterval');
  const { input: conditionsInput } = useField<string>('conditions');
  const { input: joinTypeInput } = useField<string>('joinType');
  const waitForJoinToDatasetToLoad = useCallback(() => {
    setIsDataLoading(true);
  }, [setIsDataLoading]);

  useEffect(
    () => {
      if (
        !initialJoinToDatasetId &&
        selectedJoinToDatasetId &&
        joinToDataset &&
        selectedJoinToDatasetId === joinToDataset.id
      ) {
        const timeSeriesJoinType = getTimeSeriesJoinType(
          stepInputDataset,
          joinToDataset,
        );
        dateIntervalInput.onChange(
          getDefaultDateInterval(stepInputDataset, joinToDataset),
        );
        conditionsInput.onChange(
          getDefaultConditions(
            timeSeriesJoinType,
            stepInputDataset,
            joinToDataset,
          ),
        );
        joinTypeInput.onChange(
          getDefaultJoinType(stepInputDataset, joinToDataset),
        );
        setIsDataLoading(false);
      }
    },
    // We only need to reset form values when join-to dataset is loaded,
    // also when other dependencies are included, it results in too many calls to this useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [joinToDataset],
  );

  return (
    <>
      <StyledH3>Select data sources</StyledH3>
      <StyledDescription>
        Choose the data sources you’d like to connect to each other.
      </StyledDescription>

      <StyledLabel>Data source 1</StyledLabel>
      <DatasetInfo dataset={stepInputDataset} isLocked />
      {!isInputTimeSeries && (
        <StyledInfoCard variant={InfoCardVariant.INFO}>
          <b>Data source 1</b> is a non-time-based data source. You will not be
          able to select a time-based data source in this connect step.
        </StyledInfoCard>
      )}

      <StyledJoinToTextContainer>
        <StyledJoinToText>connect to</StyledJoinToText>
      </StyledJoinToTextContainer>

      <StyledFormGroup>
        <JoinToDatasetPicker
          isJoinToDatasetLoading={isLoading}
          joinToDatasetLoadingError={
            isError
              ? "Couldn't load information about this data source."
              : undefined
          }
          isNewStep={isNewStep}
          onAfterChange={waitForJoinToDatasetToLoad}
          organizationId={workflow.organizationId}
          stepInputDataset={stepInputDataset}
          workflowOutputDataset={workflow.outputDataset}
          selectedJoinToDataset={joinToDataset}
        />
      </StyledFormGroup>
    </>
  );
}

export function JoinToModalPage(props: JoinToModalPageProps) {
  return (
    <ErrorBoundary>
      <JoinToModalPageContent {...props} />
    </ErrorBoundary>
  );
}
